import React, { useCallback, useEffect, useRef, useState } from 'react'
import { colors } from '@pff-consumer/core-ui'
import { getCouponInfo } from '@pff-consumer/utils/lib/accounts/get-coupon-info'
import { getPromoCodeInputValue } from '@pff-consumer/utils/lib/accounts/get-promo-code-input-value'
import { Icon } from '@pff-consumer/tweed-ui/atoms/icon/icon.web'
import { AccountPages } from '@pff-consumer/schema'
import { useFeatureFlags } from '@pff-consumer/feature-flags'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/text'
import { useSubscribeAnalyticsEvent } from '../../hooks/use-subscribe-analytics-event.web'
import { checkoutHooks } from '@pff-consumer/data-provider/index.checkout'
import { SVGGroup } from '@pff-consumer/utils'
import { getIdMeRedirectUrl } from '@pff-consumer/utils/lib/accounts/get-id-me-redirect-url'
import { useStyles } from './promo-code.styles'
import { PromoCodeProps } from './promo-code.props'
import { LEGACY_CHECKOUT_URL } from '../../shared/constants'

const { useCheckoutActions, usePromoCode, useGetIsAccountsPage, useGetIsLoggedIn } = checkoutHooks

export const PromoCode = (props: PromoCodeProps) => {
  const { isCouponValid, isDiscountApplied = false } = props
  const { updatePromoCode } = useCheckoutActions()
  const savedPromoCode = usePromoCode()
  const isAccountsPage = useGetIsAccountsPage()
  const isLoggedIn = useGetIsLoggedIn()
  const [isActive, setIsActive] = useState<boolean>(false)
  const [promoCode, setPromoCode] = useState<string>(savedPromoCode)
  const { message, isIdme } = getCouponInfo(savedPromoCode)

  const styles = useStyles({ isIdme })
  const inputRef = useRef<HTMLInputElement>(null)
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const { getFeatureFlag } = useFeatureFlags()
  const isIdMeVerificationEnabled = getFeatureFlag('show-id-me-verify-button', false)
  const ctaLocation = isAccountsPage ? AccountPages.Subscription : 'Single Page Checkout'
  const activeTabIndex = isActive ? 0 : -1

  const promoCodeButtonStyles = `${styles.promoCodeButton} ${isActive ? styles.promoCodeButtonActive : ''}`
  const innerContainerStyles = `${styles.innerContainer} ${isActive ? styles.innerContainerActive : ''}`
  const promoCodeTextFieldStyles = `${styles.promoCodeTextField} ${!isCouponValid ? styles.promoCodeTextFieldError : ''}`
  const containerStyles = `${styles.container} ${isActive ? '' : styles.inactiveContainer}`

  /**
   * When user decides to login after applying promo code, this useeffect will restore the promocode
   */
  useEffect(() => {
    if (savedPromoCode) {
      setPromoCode(savedPromoCode)
      setIsActive(!!savedPromoCode)
    }
  }, [savedPromoCode])

  const togglePromoCodeAccordion = useCallback(() => {
    setIsActive(!isActive)
    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: 'Promo Code',
      ctaLocation: ctaLocation,
    })
  }, [isActive, ctaLocation, sendSubscribeAnalyticsEvent])

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value
    setPromoCode(value)
    if (value === '') {
      updatePromoCode('')
    }
  }

  const clearPromoCode = () => {
    setPromoCode('')
    updatePromoCode('')
  }

  const onClearKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      clearPromoCode()
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }
  }

  const applyPromoCode = useCallback(() => {
    updatePromoCode(promoCode)
    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: 'Apply',
      ctaLocation: ctaLocation,
    })
  }, [promoCode, ctaLocation, updatePromoCode, sendSubscribeAnalyticsEvent])

  const handleIdMeVerifyButton = (e: React.MouseEvent) => {
    e.preventDefault()
    sendSubscribeAnalyticsEvent('CTA Clicked', {
      ctaName: 'Verify with id.me',
      ctaLocation: ctaLocation,
    })
    const oAuthRedirectUrl = getIdMeRedirectUrl(
      isLoggedIn,
      window.location.href,
      process.env.NEXT_PUBLIC_IDME_CLIENT_ID
    )
    if (isIdMeVerificationEnabled) {
      window.open(oAuthRedirectUrl, '_self')
    } else {
      window.open(LEGACY_CHECKOUT_URL, '_blank')
    }
  }

  return (
    <div className={containerStyles}>
      <button
        type='button'
        onClick={togglePromoCodeAccordion}
        className={promoCodeButtonStyles}
        data-analytics-id='promo-code-button'
      >
        <Text
          colorOverride={colors.webLinkText}
          textAlign='center'
          variant={TextDescriptionVariant.TEXT_SMALL_REGULAR}
        >
          Promo Code
        </Text>
        <Icon
          name={isActive ? 'collapse' : 'expand'}
          group={SVGGroup.Icons}
          height={25}
          width={25}
        />
      </button>
      <div className={innerContainerStyles}>
        <div className={styles.promoCodeContainer}>
          <div className={styles.promoCodeTextFieldContainer}>
            <input
              placeholder='Enter promo code'
              aria-label='Promo Code'
              className={promoCodeTextFieldStyles}
              tabIndex={activeTabIndex}
              value={getPromoCodeInputValue(promoCode)}
              onChange={handlePromoCodeChange}
              ref={inputRef}
            />

            {promoCode ? (
              <span
                role='button'
                className={styles.promoCodeTextFieldClear}
                tabIndex={activeTabIndex}
                aria-label='Clear promo code'
                onClick={clearPromoCode}
                onKeyDown={onClearKeyDown}
                data-analytics-id='clear-promo-button'
              >
                <Text
                  textAlign='center'
                  colorOverride={colors.webFadedTextColor}
                  variant={TextDescriptionVariant.TEXT_SMALL_REGULAR}
                >
                  Clear
                </Text>
              </span>
            ) : null}
          </div>
          <button
            type='button'
            className={`${styles.promoCodeApplyButton} ${isDiscountApplied ? styles.promoCodeApplied : ''}`}
            onClick={applyPromoCode}
            tabIndex={activeTabIndex}
            disabled={isDiscountApplied}
            data-analytics-id='promo-code-apply-button'
            data-testid='promo-code-apply-button'
          >
            <Text
              colorOverride={isDiscountApplied ? colors.lightTextColor : colors.webInverseTextColor}
              variant={TextDescriptionVariant.TEXT_BASE_SEMIBOLD}
            >
              {isDiscountApplied ? 'Applied' : 'Apply'}
            </Text>
          </button>
        </div>
        {!isCouponValid ? (
          <div className={styles.errorContainer}>
            <Icon
              name='field-error'
              group={SVGGroup.Icons}
              height={12}
              width={12}
            />
            <Text
              colorOverride={colors.webErrorColor}
              variant={TextDescriptionVariant.TEXT_XS_REGULAR_ITALIC}
            >
              Promo code is not valid
            </Text>
          </div>
        ) : null}
        {isDiscountApplied && (
          <div className={styles.errorContainer}>
            <Icon
              name='field-success'
              group={SVGGroup.Icons}
              height={12}
              width={12}
            />
            <Text
              colorOverride={colors.validPasswordColor}
              variant={TextDescriptionVariant.TEXT_XS_REGULAR_ITALIC}
            >
              {message}
            </Text>
          </div>
        )}
        <div className={styles.promoCodeApplicabilityContainer}>
          <Text
            colorOverride={colors.webFadedTextColor}
            variant={TextDescriptionVariant.TEXT_2XS_REGULAR}
          >
            Military, First Responder & Student 50% Discount
          </Text>

          <button
            type='button'
            onClick={handleIdMeVerifyButton}
            tabIndex={activeTabIndex}
            data-testid='idMe.verify.button'
            aria-label='Idme'
            className={styles.verifyButton}
          >
            <Icon
              name='id-me'
              group={SVGGroup.Icons}
              height={25}
              width={128}
            />
          </button>
        </div>
      </div>
    </div>
  )
}
